/**
 * Settings for Lia notifications by screen.
 * Each screen can have a custom notification with message and action when clicking.
 *
 * To add or change notifications:
 * 1. Add messageKey and actionMessageKey translations in pt_BR.json, en_US.json and es_MX.json
 * 2. Add or edit the corresponding entry in this file
 */

export interface LiaNotificationConfig {
  /** Translation key for the message (uses {{firstName}} for customization) */
  messageKey: string;
  /** Translation key for the message sent to the IA when the user clicks the notification */
  actionMessageKey: string;
}

/** Key used in the cache to identify each screen */
export function getScreenKey(pathname: string): string {
  const normalized =
    pathname.replace(/\/+$/, "").replace(/^\/(demo\/)?/, "") || "index";
  return normalized || "index";
}

/**
 * Map of paths for notification configuration.
 * Order: the most specific path (longest) has priority.
 * Use paths without initial slash.
 */
const LIA_NOTIFICATIONS_BY_PATH: Record<string, LiaNotificationConfig> = {
  painel: {
    messageKey: "chatbot-notification-spaces",
    actionMessageKey: "chatbot-notification-action-spaces",
  },
  insights: {
    messageKey: "chatbot-notification-insights",
    actionMessageKey: "chatbot-notification-action-insights",
  },
  "company/members": {
    messageKey: "chatbot-notification-members",
    actionMessageKey: "chatbot-notification-action-members",
  },
  "company/groups": {
    messageKey: "chatbot-notification-members",
    actionMessageKey: "chatbot-notification-action-members",
  },
  "company/subscribers": {
    messageKey: "chatbot-notification-members",
    actionMessageKey: "chatbot-notification-action-members",
  },
  "company/import": {
    messageKey: "chatbot-notification-import",
    actionMessageKey: "chatbot-notification-action-import",
  },
  "reasons-of-use": {
    messageKey: "chatbot-notification-reasons",
    actionMessageKey: "chatbot-notification-action-reasons",
  },
  payments: {
    messageKey: "chatbot-notification-costs",
    actionMessageKey: "chatbot-notification-action-costs",
  },
  contracts: {
    messageKey: "chatbot-notification-contracts",
    actionMessageKey: "chatbot-notification-action-contracts",
  },
  packages: {
    messageKey: "chatbot-notification-packages",
    actionMessageKey: "chatbot-notification-action-packages",
  },
  "demo/packages": {
    messageKey: "chatbot-notification-packages",
    actionMessageKey: "chatbot-notification-action-packages",
  },
  "demo/contracts": {
    messageKey: "chatbot-notification-contracts",
    actionMessageKey: "chatbot-notification-action-contracts",
  },
  "private-offices": {
    messageKey: "chatbot-notification-private-offices",
    actionMessageKey: "chatbot-notification-action-private-offices",
  },
  "demo/private-offices": {
    messageKey: "chatbot-notification-private-offices",
    actionMessageKey: "chatbot-notification-action-private-offices",
  },
};

const DEFAULT_NOTIFICATION: LiaNotificationConfig = {
  messageKey: "chatbot-notification-spaces",
  actionMessageKey: "chatbot-notification-action-spaces",
};

/**
 * Returns the notification configuration for the current path.
 * Makes match by prefix (the most specific path has priority).
 * Returns the default configuration when there is no match.
 */
export function getNotificationForPath(
  pathname: string,
): LiaNotificationConfig {
  const normalized = pathname.replace(/\/+$/, "").replace(/^\//, "");
  if (!normalized) return DEFAULT_NOTIFICATION;

  const segments = normalized.split("/").filter(Boolean);
  let bestMatch: LiaNotificationConfig | null = null;
  let bestMatchLength = 0;

  for (const [path, config] of Object.entries(LIA_NOTIFICATIONS_BY_PATH)) {
    const configSegments = path.split("/").filter(Boolean);
    const pathMatches =
      segments.length >= configSegments.length &&
      configSegments.every((s, i) => segments[i] === s);
    if (pathMatches && configSegments.length > bestMatchLength) {
      bestMatch = config;
      bestMatchLength = configSegments.length;
    }
  }

  return bestMatch ?? DEFAULT_NOTIFICATION;
}
